import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function NewQuoteModal({ open, onClose, onCreated }) {
  const { API } = useAuth();
  const [form, setForm] = useState({ company: "", contact_name: "", contact_email: "", industry: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/quotes`, form, { withCredentials: true });
      setForm({ company: "", contact_name: "", contact_email: "", industry: "" });
      onCreated(data.quote);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4" data-testid="new-quote-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#111C33]">
        <h3 className="text-lg font-bold text-slate-950 dark:text-white">New Quote</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Create a Draft quote. You can add pricing and details after.</p>
        <div className="mt-5 space-y-3">
          <input
            required
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="Company"
            data-testid="new-quote-company"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
          <input
            required
            value={form.contact_name}
            onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            placeholder="Contact name"
            data-testid="new-quote-contact-name"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
          <input
            required
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            placeholder="Contact email"
            data-testid="new-quote-contact-email"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
          <input
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            placeholder="Industry (optional)"
            data-testid="new-quote-industry"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
        </div>
        {error && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="new-quote-error">{error}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="new-quote-submit-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Create Draft
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="new-quote-cancel-btn"
            className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
